<script setup lang="ts">
import {computed, type PropType, ref, watch} from 'vue';
import {ElButton, ElDialog, ElMessage, ElSwitch} from 'element-plus';
import {type CabUser, type SessieInfo, type SessieTelling} from 'common';
import {USER_CHANGE_EMIT, USER_SESSIONS_ENDED_EMIT} from '@/base/cabConstants';
import {endSession, endSessions, sendPasswordMail, sessiesVan} from '@/base/callBackend';
import {renderDateTimeLocal} from '@/base/utils';

const visible = defineModel<boolean>({required: true});
const emits   = defineEmits([USER_CHANGE_EMIT, USER_SESSIONS_ENDED_EMIT]);
const props   = defineProps({
    user: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    telling: {
        type    : Object as PropType<SessieTelling>,
        required: false,
    },
});

const sessies     = ref<SessieInfo[]>([]);
const sendingMail = ref<boolean>(false);
const bezig       = ref<boolean>(false);

const laatsteLogin = computed<string>(() => props.user.lastLoginAt ? renderDateTimeLocal(props.user.lastLoginAt) : 'nog nooit');
const browsers     = computed<number>(() => props.telling?.browsers ?? 0);
const nietsTeDoen  = computed<boolean>(() => sessies.value.length === 0 && browsers.value === 0);

watch(visible, async (open: boolean) => {
    if (open) {
        await laadSessies();
    }
});

async function laadSessies() {
    if (!props.user.email) {
        sessies.value = [];
        return;
    }
    try {
        sessies.value = await sessiesVan(props.user.email);
    } catch (error: any) {
        ElMessage.error(`Sessies van ${props.user.email} niet geladen`);
    }
}

function onTweeFactor() {
    emits(USER_CHANGE_EMIT, props.user);
}

async function onSendPasswordMail() {
    sendingMail.value = true;
    try {
        await sendPasswordMail(props.user.email!);
        ElMessage.success(`Mail verstuurd naar ${props.user.email}`);
    } catch (error: any) {
        ElMessage.error(`Mail niet verstuurd naar ${props.user.email}`);
    } finally {
        sendingMail.value = false;
    }
}

async function onEndSession(sessie: SessieInfo) {
    await beeindig(() => endSession(props.user.email!, sessie.id), 'Sessie beeindigd', 'Sessie niet beeindigd');
}

async function onEndSessions() {
    await beeindig(() => endSessions(props.user.email!), `Sessies beeindigd van ${props.user.email}`, `Sessies niet beeindigd van ${props.user.email}`);
}

async function beeindig(actie: () => Promise<void>, gelukt: string, mislukt: string) {
    bezig.value = true;
    try {
        await actie();
        ElMessage.success(gelukt);
        await laadSessies();
        emits(USER_SESSIONS_ENDED_EMIT);
    } catch (error: any) {
        ElMessage.error(mislukt);
    } finally {
        bezig.value = false;
    }
}
</script>

<template>
    <el-dialog
        v-model="visible"
        :show-close="false"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
        width="900px"
        data-testid="user-auth-dialog"
    >
        <template #header>
            <div class="text-xl text-center w-full bg-gray-700 text-gray-200 py-2">
                {{ user.name }} <span class="text-sm text-gray-400">{{ user.email }}</span>
            </div>
        </template>
        <div class="flex flex-col bg-gray-300 p-3 space-y-3 text-sm text-gray-900">
            <div class="flex flex-row items-center bg-white p-2">
                <div class="w-48">Inlogcode per mail (2FA)</div>
                <el-switch v-model="user.tweeFactor" data-testid="two-factor-switch" @change="onTweeFactor"/>
            </div>
            <div class="flex flex-row items-center bg-white p-2">
                <div class="w-48">Laatst ingelogd</div>
                <div data-testid="last-login">{{ laatsteLogin }}</div>
            </div>
            <div class="flex flex-row items-center bg-white p-2">
                <div class="w-48">Wachtwoord</div>
                <el-button
                    size="small"
                    :disabled="!user.email"
                    :loading="sendingMail"
                    title="mail versturen waarmee de gebruiker zelf een wachtwoord kan instellen"
                    data-testid="send-password-mail-button"
                    @click="onSendPasswordMail"
                >
                    Wachtwoordmail sturen
                </el-button>
            </div>
            <div class="flex flex-col bg-white p-2">
                <div class="flex flex-row items-center mb-1">
                    <div class="w-48">Sessies</div>
                    <div class="flex-1 text-xs text-gray-500">{{ browsers }} vertrouwde browser{{ browsers === 1 ? '' : 's' }}</div>
                    <el-button
                        type="primary"
                        size="small"
                        :disabled="!user.email || nietsTeDoen"
                        :loading="bezig"
                        title="alle sessies beeindigen en alle vertrouwde browsers vergeten"
                        data-testid="end-sessions-button"
                        @click="onEndSessions"
                    >
                        Alle sessies beeindigen
                    </el-button>
                </div>
                <div v-if="sessies.length === 0" class="text-xs text-gray-500">
                    geen actieve sessies
                </div>
                <table v-else class="table-auto border-collapse border w-full text-xs">
                    <thead>
                    <tr class="text-gray-500">
                        <th class="border p-1 text-left font-normal">aangemaakt</th>
                        <th class="border p-1 text-left font-normal">laatst gezien</th>
                        <th class="border p-1 text-left font-normal">ip</th>
                        <th class="border p-1 text-left font-normal">browser</th>
                        <th class="border p-1"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="sessie in sessies" :key="sessie.id" data-testid="sessie-regel">
                        <td class="border p-1 whitespace-nowrap">{{ renderDateTimeLocal(new Date(sessie.aangemaaktOp)) }}</td>
                        <td class="border p-1 whitespace-nowrap">{{ renderDateTimeLocal(new Date(sessie.laatstGezienOp)) }}</td>
                        <td class="border p-1 whitespace-nowrap">{{ sessie.ip }}</td>
                        <td class="border p-1 max-w-80 truncate" :title="sessie.browser">{{ sessie.browser }}</td>
                        <td class="border p-1 whitespace-nowrap text-right">
                            <span v-if="sessie.huidig" class="mr-2 text-gray-500">deze sessie</span>
                            <el-button size="small" :loading="bezig" @click="onEndSession(sessie)">
                                beeindigen
                            </el-button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </el-dialog>
</template>
